<template>
  <UIDialog
    :is-open
    :z-index
    size="full"
    title="Organize Family Structure"
    @close="handleClose"
  >
    <div class="reorganize-tree">
      <div ref="panelRef" class="reorganize-tree__panel">
        <UIPreloader v-if="isTreeLoading" message="Loading tree..." class="reorganize-tree__preloader" />
        <ReorganizeTreeNode
          v-else-if="!isEmptyTree"
          :tree="tree as ReorganizeTreeItem"
          :path="[AnalyticsField.REPO_ROOT]"
          :amounts-map
          :entry-settings
          @click:create="handleCreateNode"
          @click:remove="handleRemoveNode"
          @click:add="handleAddNode"
          @click:delete="handleDeleteNode"
          @choose="handleChoose"
        />
        <div v-else class="reorganize-tree__panel-empty">
          <span>You just need to add at least one <br />asset to the tree</span>
          <UIButton
            label="first asset"
            variant="secondary"
            size="sm"
            :leading="PlusIcon"
            @click="handleCreateNode([AnalyticsField.REPO_ROOT])"
          />
        </div>
      </div>
      <div class="reorganize-tree__content">
        <div class="reorganize-tree__transactions">
          <UIPreloader v-if="isTreeLoading" message="Loading tree..." class="reorganize-tree__preloader" />
          <ReorganizeTreeTransactions v-else ref="transactionsRef" :transactions="transactions as TransactionClass[]" />
        </div>
        <div class="reorganize-tree__historic">
          <ReorganizeTreeHistoric />
        </div>
      </div>
      <UIPreloader v-if="isSaving" message="Saving tree..." overlay  />
    </div>
    <template #footer>
      <UIButtonsGroup>
        <UIButtonSave
          label="Save"
          :loading="isSaving"
          :disabled="!hasUnsaved || isSaving"
          @click="handleClickSave"
        />
        <UIButtonClose :disabled="isSaving" @click="handleClose" />
      </UIButtonsGroup>
    </template>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, ref, markRaw, onMounted, provide, onBeforeUnmount, useTemplateRef, watch } from 'vue'
import { get, set, unset } from 'lodash'

import { AnalyticsField, ModalClass, TransactionClass } from '@types'
import { ReorganizeTreeItem } from './utils/types'

import { ASSET_FIELD, UNKNOWN_VALUE } from '@/const'

import { generateId } from '@/entities/utils/helpers'
import { getCurrentDate } from '@/helpers/dates'
import { handleCatchedError } from '@/helpers/common'

import { useNotifications } from '@/plugins/notification'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useAssetsStore } from '@/store/assets'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesStore } from '@/store/repositories'
import { useTransactionsStore } from '@/store/transactions'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import ReorganizeTreeNode from './ReorganizeTreeNode.vue'
import ReorganizeTreeHistoric from './ReorganizeTreeHistoric.vue'
import ReorganizeTreeTransactions from './ReorganizeTreeTransactions.vue'
import { UIPreloader } from '@ui/core'
import { UIDialog } from '@ui/modals'
import { UIButton, UIButtonClose, UIButtonsGroup, UIButtonSave } from '@ui/buttons'
import { PlusIcon } from '@heroicons/vue/24/outline'

import NodeForm from './components/NodeForm.vue'
import PrecisionForm from './components/PrecisionForm.vue'

type Props = {
  modal: ModalClass<any>
}

const props = defineProps<Props>()

const { warn, error, success } = useNotifications()

const analyticsStore = useAnalyticsStore()
const assestsBunch = useAssetsBunchStore()
const assetsStore = useAssetsStore()
const modalsStore = useModalsStore()
const repositoriesStore = useRepositoriesStore()
const transactionsSettingsStore = useTransactionsSettingsStore()
const transactionsStore = useTransactionsStore()

const tree = ref<ReorganizeTreeItem>({})

const amountsMap = ref<Map<string, number>>(new Map())
provide('amountsMap', amountsMap)

const transactions = ref<TransactionClass[]>([])

const chosenPath = ref<string[]>()

const justCreatedPath = ref<string>()
provide('justCreatedPath', justCreatedPath)

const isSaving = ref(false)

const panelRef = useTemplateRef('panelRef')
const transactionsRef = useTemplateRef('transactionsRef')

const repositoryId = computed(() => repositoriesStore.getCurrentId)

const isTreeLoading = computed(() => analyticsStore.module.tree.isLoading)

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const analyticsTree = computed(() => analyticsStore.module.tree.getList)

const analyticsDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

const isEmptyTree = computed(() => !Object.keys(tree.value?.[AnalyticsField.REPO_ROOT] || {}).length)

const hasUnsaved = computed(() => transactions.value.length)

const entrySettings = computed(() => transactionsSettingsStore.getList.find(item => item.name === 'add account')?.entries[0])

const accountTypes = computed(() => entrySettings.value?.account?.includes || [])
const assetTypes = computed(() => entrySettings.value?.asset?.includes || [])

const getAssetName = (id: string) => {
  if (id === AnalyticsField.REPO_ROOT) return repositoriesStore.getCurrentName
  const instance = assestsBunch.getElementById(id)
  return instance?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE
}
provide('getAssetName', getAssetName)

const createTransactionInstance = (accountId: string, assetId: string, amount = 1): TransactionClass | undefined => {
  if (accountId === AnalyticsField.REPO_ROOT) return
  const instance = new TransactionClass({
    id: generateId(),
    date: analyticsDate.value,
    type: 'add account',
    description: '',
    entries: [
      {
        account_id: accountId,
        amount,
        asset_id: assetId,
      }
    ],
    repository_id: repositoryId.value,
  }, true, true)
  return instance
}

const handleCreateNode = (path: string[], callback?: () => void) => {
  const accountId = path.at(-1)
  if (!accountId) {
    error({ message: 'Account ID is required' })
    return
  }
  let restrictions: string[] = []
  if (path.length === 1) {
    restrictions = accountTypes.value
  } else {
    restrictions = assetTypes.value
  }
  // create new asset
  const modalInstance = modalsStore.init('create-new-node', markRaw(NodeForm))
  // open node form
  modalInstance?.open(modalsStore.getZIndex(), {
    title: `Add child to ${getAssetName(path.at(-1) || '')}`,
    restrictions,
    onComplete: (assetId: string) => {
      if (assetId === accountId) {
        error({ message: 'You cannot add a node to itself' })
        chosenPath.value = undefined
        return
      }
      const newPath = [...path, assetId]
      justCreatedPath.value = JSON.stringify(newPath)
      let transactionInstance: TransactionClass | undefined
      // check if the node is already in the tree
      const children = get(tree.value, newPath)
      if (children === undefined) {
        // add transaction
        transactionInstance = createTransactionInstance(accountId, assetId)
        const amount = transactionInstance?.entry1().value?.amount
        // add amount for the node
        amountsMap.value.set(newPath.join('-'), amount ?? 0)
        set(tree.value, newPath, {})
      }
      // check precision amount
      checkPrecisionAmount(transactionInstance, assetId, () => {
        if (transactionInstance) {
          transactions.value.push(transactionInstance)
          transactionsRef.value?.scrollToBottom()
        }
      })
      setTimeout(() => {
        panelRef.value?.querySelector('.reorganize-tree-node--just-created')?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })
        justCreatedPath.value = undefined
        callback?.()
      })
    },
  })
}

const handleAddNode = (path: string[]) => {
  // get asset id and account id
  const assetId = path.at(-1)
  const accountId = path.at(-2)
  if (!assetId || !accountId) {
    error({ message: 'Asset ID and account ID are required' })
    return
  }
  if (assetId === accountId) {
    error({ message: 'You cannot add a node to itself' })
    chosenPath.value = undefined
    return
  }
  let amount = 0
  // get old node from chosen path
  let newNode = get(tree.value, chosenPath.value || []) || {}
  // check if the node is already in the tree
  const children = get(tree.value, path)
  if (children !== undefined) {
    amount = amountsMap.value.get(path.join('-')) || 0
    newNode = {
      ...newNode,
      ...children,
    }
  }
  // pluse amount from chosen path
  let chosenAmount = amountsMap.value.get(chosenPath.value?.join('-') || '') || 0
  amount += chosenAmount
  // add new amount to amounts map
  amountsMap.value.set(path.join('-'), amount)
  // add new node to tree
  set(tree.value, path, newNode)
  // check if transaction for this node already exists
  const transaction = transactions.value.find(transaction => transaction.entry1().value?.account_id === accountId && transaction.entry1().value?.asset_id === assetId)
  if (transaction) {
    const transactionAmount = transaction.entry1().value?.amount ?? 0
    chosenAmount += Math.round(transactionAmount * 100) / 100
    transactions.value = transactions.value.filter(item => item.id !== transaction.id)
    if (chosenAmount === 0) return
  }
  // add new transaction
  const transactionInstance = createTransactionInstance(accountId, assetId, chosenAmount)
  if (transactionInstance) {
    transactions.value.push(transactionInstance)
    transactionsRef.value?.scrollToBottom()
  }
}

const handleDeleteNode = (path: string[], cascade = true) => {
  const assetId = path.at(-1)
  const accountId = path.at(-2)
  if (!assetId || !accountId) {
    error({ message: 'Asset ID and account ID are required' })
    chosenPath.value = undefined
    return
  }
  // remove node from tree
  const parentPath = path.slice(0, -1)
  if (cascade) {
    // remove children nodes
    const children = get(tree.value, path)
    if (children) {
      Object.keys(children).forEach(child => {
        handleDeleteNode([...path, child], true)
      })
    }
  }
  const node = get(tree.value, parentPath)
  if (node) {
    unset(node, assetId)
    set(tree.value, parentPath, node)
  }
  // get amount for the node
  const amount = amountsMap.value.get(path.join('-')) || 1
  amountsMap.value.delete(path.join('-'))
  // check if transaction for this node already exists
  const transactionIndex = transactions.value.findIndex(transaction => transaction.entry1().value?.account_id === accountId && transaction.entry1().value?.asset_id === assetId)
  if (transactionIndex !== -1) {
    transactions.value = transactions.value.filter((_, index) => index !== transactionIndex)
    return
  }
  // add transaction with negative amount
  const transactionInstance = createTransactionInstance(accountId, assetId, -amount)
  if (transactionInstance) {
    transactions.value.push(transactionInstance)
    transactionsRef.value?.scrollToBottom()
  }
}

const handleRemoveNode = (path: string[]) => {
  if (!chosenPath.value) return
  handleDeleteNode(path, false)
  chosenPath.value = undefined
}

const handleChoose = (path: string[]) => {
  chosenPath.value = path
}

const handleClickSave = async () => {
  try {
    isSaving.value = true
    // store new transactions
    await transactionsStore.bulkStore(transactions.value.map(instance => instance.get()))
    // refresh data
    await Promise.all([assetsStore.fetch(), transactionsStore.fetch()])
    success({ message: 'Tree saved successfully' })
    props.modal.close()
  } catch (e) {
    if (!e) return
    const message = (e as Error).message
    error({ message })
    handleCatchedError(message)
  } finally {
    isSaving.value = false
  }
}

const handleClose = () => {
  if (!hasUnsaved.value) {
    props.modal.close()
    return
  }
  warn({
    message: 'Warning',
    description:
      'You have unsaved changes in the tree, would you like to continue editing?',
    actionRequired: true,
    actions: [
      {
        label: 'Continue editing',
        buttonVariant: 'primary',
        onClick: remove => {
          remove && remove()
        },
      },
      {
        label: 'Discard changes',
        buttonVariant: 'secondary',
        onClick: remove => {
          remove && remove()
          props.modal.close()
        },
      },
    ],
  })
}

const checkPrecisionAmount = (transaction: TransactionClass | undefined, assetId: string, callback: () => void) => {
  const accountId = transaction?.entry1().value?.account_id
  const amounts = Array.from(amountsMap.value.entries()).filter(([key]) => key.endsWith(assetId))
  if (amounts.length < 2) {
    callback()
    return
  }
  // open amount form
  const modalInstance = modalsStore.init('precision-form', markRaw(PrecisionForm))
  modalInstance?.open(modalsStore.getZIndex(), {
    entrySettings: entrySettings.value,
    amounts,
    assetId,
    createTransactionInstance,
    onComplete: (result: [string, TransactionClass][]) => {
      result.forEach(([path, item]) => {
        const itemAccountId = item.entry1().value?.account_id
        const itemAssetId = item.entry1().value?.asset_id
        // if this is current transaction, update amount
        if (transaction && itemAccountId === accountId && itemAssetId === assetId) {
          const amount = item.entry1().value?.amount ?? 0
          // update amount for the transaction
          transaction.entry1().value.amount = amount
          // update amount for the amounts map
          amountsMap.value.set(path, amount)
        } else if (itemAccountId && itemAssetId) {
          const oldAmount = amountsMap.value.get(path) ?? 0
          const newAmount = item.entry1().value?.amount ?? 0
          // update amount for the transaction
          item.entry1().value.amount = newAmount - oldAmount
          // add new transaction to transactions list
          transactions.value.push(item)
          // update amount for the amounts map
          amountsMap.value.set(path, newAmount)
        }
      })
    },
    onCancel: callback,
  })
}

watch(() => ({ data: analyticsTree.value, settingsInitFlag: transactionsSettingsStore.initFlag }), async ({ data, settingsInitFlag }) => {
  if (!data || !settingsInitFlag) return
  // shrink data to remove all leaf nodes
  const parentIds = new Set<string>()
  data.forEach(item => {
    const parentId = item.path.at(-2)
    if (typeof parentId === 'string') {
      parentIds.add(parentId)
    }
  })
  const shrinkData = data.filter(item => {
    const assetId = item.path.at(-1)
    return typeof assetId === 'string' && parentIds.has(assetId)
  })
  // create tree structure with existing analytics tree
  shrinkData.forEach(item => {
    const accountId = item.path.length > 2 ? item.path.at(-2) : AnalyticsField.REPO_ROOT
    const assetId = item.path.at(-1)
    if (!assetId || !accountId) return
    // check if asset is in types list
    const instance = assestsBunch.getElementById(assetId)
    if (!instance) return
    const assetType = instance.field<string>(ASSET_FIELD.TYPE).value
    if (![...accountTypes.value, ...assetTypes.value].includes(assetType)) return
    set(tree.value, item.path, {})
    // set amount for the node
    amountsMap.value.set(item.path.join('-'), item.amount)
  })
}, { immediate: true })

onMounted(async () => {
  analyticsStore.setActive('ReorganizeTree', ['tree'])
  if (!transactionsSettingsStore.initFlag) {
    await transactionsSettingsStore.fetch()
  }
})

onBeforeUnmount(() => {
  analyticsStore.setInActive('ReorganizeTree')
})
</script>

<style scoped>
.reorganize-tree {
  @apply min-h-96 flex-auto;
  @apply grid grid-cols-1 sm:grid-cols-[minmax(350px,35%)_auto] gap-4;
  @apply -mb-4 -mx-3 sm:mx-0;

  &__panel,
  &__transactions,
  &__historic {
    @apply p-2 sm:p-4;
    @apply border border-light-border;
    @apply bg-body-gray;
    @apply rounded-lg;
  }

  &__panel {
    @apply min-h-[200px];
    @apply overflow-y-auto;
  }

  &__content {
    @apply min-h-[600px] sm:min-h-0;
    @apply flex flex-col gap-4;
    @apply overflow-hidden;
  }

  &__transactions {
    @apply h-1/2;
    @apply overflow-hidden;
  }

  &__historic {
    @apply h-1/2;
    @apply flex flex-col px-0 pb-0;
    @apply overflow-hidden;

    :deep(.ui-panel) {
      @apply px-2 sm:px-4 pb-4;
    }
  }

  &__panel-empty {
    @apply h-full;
    @apply flex flex-col items-center justify-center;
    @apply gap-4;

    span {
      @apply text-xs;
      @apply text-light-text;
      @apply text-center;
    }
  }

  &__preloader {
    @apply w-full h-full;
    @apply flex flex-col items-center justify-center;
  }
}
</style>
